import { Col, Input, Row, Spin } from "antd";
import queryClient from "api/queryClient";
import { useRegisterUserMutation } from "api/User/hooks.ts";
import { AuthAlert } from "components/UI/AuthAlert/AuthAlert.tsx";
import { AuthLink } from "components/UI/AuthLink/AuthLink.tsx";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { RegistrationSuccess } from "components/UI/RegistrationSuccess/RegistrationSuccess.tsx";
import { AuthFormWrapper } from "components/Wrappers/AuthFormWrapper/AuthFormWrapper";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { FC, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { Trans } from "react-i18next";
import { RegistrationPasswordType } from "types/enums";
import { extractValidationErrorMessages } from "utils/extractValidationErrorMessages.ts";

import { RegistrationFormData, schema } from "./formSchema";
import { useEmailAvailability } from "./useEmailAvailability";

const EMAIL_CHECK_DEBOUNCE_MS = 600;

interface RegistrationFormContentProps {
    submitError?: string[] | null;
    isSubmitting?: boolean;
}

export const RegistrationFormContent: FC<RegistrationFormContentProps> = ({ submitError, isSubmitting }) => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");
    const { t: tCommon } = usePaldockTranslation("common");
    const generalFormUtils = useGeneralForm<RegistrationFormData>();
    const { control, fieldNames, formState } = generalFormUtils;
    const { emailTaken, emailAvailable, isCheckingEmail, checkEmail, resetStatus } = useEmailAvailability();
    const emailValue = useWatch({ control, name: fieldNames.email.name });
    const isEmailValid = Boolean(emailValue) && !formState.errors.email;

    useEffect(() => {
        if (!isEmailValid) {
            return;
        }
        const timer = setTimeout(() => checkEmail(emailValue), EMAIL_CHECK_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [emailValue, isEmailValid]);

    return (
        <AuthFormWrapper
            buttonText={tCommon("register")}
            loading={isSubmitting}
            buttonDisabled={!emailAvailable || isSubmitting}
        >
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <FormItem
                        control={control}
                        name={fieldNames.email.name}
                        label={tFields("email.label")}
                        required
                        className="!mb-0"
                    >
                        <Input
                            type="email"
                            placeholder={tFields("email.placeholder")}
                            status={emailTaken ? "error" : undefined}
                            suffix={isCheckingEmail ? <Spin size="small" /> : undefined}
                            onChange={() => resetStatus()}
                            onBlur={() => isEmailValid && checkEmail(emailValue)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (isEmailValid) {
                                        checkEmail(emailValue);
                                    }
                                }
                            }}
                        />
                    </FormItem>
                </Col>
                {emailTaken && (
                    <Col span={24}>
                        <AuthAlert
                            type="error"
                            message={
                                <Trans
                                    i18nKey="forms.registration.messages.email_already_registered"
                                    components={{ a: <AuthLink to="/auth/login" /> }}
                                />
                            }
                        />
                    </Col>
                )}
                {emailAvailable && (
                    <Col span={24}>
                        <FormItem
                            control={control}
                            name={fieldNames.nickname.name}
                            label={tFields("nickname.label")}
                            required
                            className="!mb-0"
                        >
                            <Input type="text" placeholder={tFields("nickname.placeholder")} />
                        </FormItem>
                    </Col>
                )}
                {submitError && submitError.length > 0 && (
                    <Col span={24}>
                        <AuthAlert
                            type="error"
                            message={
                                submitError.length === 1 ? (
                                    submitError[0]
                                ) : (
                                    <ul className="list-disc pl-4">
                                        {submitError.map((message) => (
                                            <li key={message}>{message}</li>
                                        ))}
                                    </ul>
                                )
                            }
                        />
                    </Col>
                )}
            </Row>
            {/* <Row gutter={16}>
                <Col span={24}>
                    <FormItem control={control} name={fieldNames.terms_agreement.name} required>
                        <Checkbox>
                            <Trans
                                i18nKey="forms.registration.terms"
                                components={{
                                    a: (
                                        <a
                                            href="https://paldock.fra1.cdn.digitaloceanspaces.com/Leadgenia.com%20VOP.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        />
                                    ),
                                }}
                            />
                        </Checkbox>
                    </FormItem>
                </Col>
            </Row> */}
        </AuthFormWrapper>
    );
};

export const RegistrationForm: FC = () => {
    const { t } = usePaldockTranslation("forms.registration");
    const { t: tAuth } = usePaldockTranslation("auth.registration");
    const { t: tCommon } = usePaldockTranslation("common");
    const [submitError, setSubmitError] = useState<string[] | null>(null);
    const [registrationSuccess, setRegistrationSuccess] = useState(false);

    const defaultValues = {
        email: "",
        nickname: "",
        terms_agreement: false,
    };

    const registerUserMutation = useRegisterUserMutation({
        onSuccess: () => {
            setSubmitError(null);
            setRegistrationSuccess(true);
            queryClient.invalidateQueries({ queryKey: ["me"] });
            queryClient.invalidateQueries({ queryKey: ["users"] });
        },
        onError: (error) => {
            setSubmitError(extractValidationErrorMessages(error));
        },
    });

    const onSubmit = (data: RegistrationFormData) => {
        setSubmitError(null);
        registerUserMutation.mutate({
            ...data,
            type: RegistrationPasswordType.WithoutPassword,
            return_url: `${import.meta.env.VITE_APP_URL}/auth/activate/{id}/{hash}`,
            invite: true,
            verified: false,
        });
    };

    if (registrationSuccess) {
        return <RegistrationSuccess message={t("messages.registration_success_message")} />;
    }

    return (
        <>
            <GeneralFormProvider<RegistrationFormData>
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmit}
                requiredMark={false}
                resetOnSubmit={false}
            >
                <RegistrationFormContent submitError={submitError} isSubmitting={registerUserMutation.isPending} />
            </GeneralFormProvider>
            <div className="flex justify-center mt-4 text-md text-black">
                {tAuth("messages.already_have_account")}{" "}
                <AuthLink to="/auth/login" className="ml-1">
                    {tCommon("login")}
                </AuthLink>
            </div>
        </>
    );
};
